import { useContext, useEffect, useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AuthContext from 'contexte/AuthContext'
import PageLoader from 'components/pageLoader/PageLoader'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import Badge from 'components/Badge/Badge'
import { launchError } from 'components/notifications'
import { fetchStructuredProducts } from 'services/structuredProductsServices'
import BBookEmpty from 'pages/bBook/components/BBookEmpty'
import { BBookPager } from 'pages/bBook/components/BBookListToolbar'
import { SP_STATUSES, statusLabel, statusColor, formatParisDate, formatAmount, getTermSheetUrl } from './components/spDisplay'

const LIMITS = [10, 25, 50]
const DEBOUNCE_MS = 300
const FILTERS = ['search', 'status', 'from', 'to']

const SpProducts = () => {
  const { user } = useContext(AuthContext)
  const id = useId()
  const [params, setParams] = useSearchParams()
  const filters = Object.fromEntries(FILTERS.map(key => [key, params.get(key) || '']))
  const page = Number(params.get('page')) || 0
  const limit = Number(params.get('limit')) || 10
  const [search, setSearch] = useState(filters.search)
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  // Un changement de filtre repart de la première page
  const update = (changes) => {
    const next = new URLSearchParams(params)
    Object.entries(changes).forEach(([key, value]) => value === '' || value === null ? next.delete(key) : next.set(key, value))
    if (!('page' in changes)) next.delete('page')
    setParams(next, { replace: true })
  }

  useEffect(() => {
    if (search === filters.search) return
    const timer = setTimeout(() => update({ search }), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchStructuredProducts({ user, page, limit, ...filters })
      .then(data => !cancelled && setProducts(data))
      .catch(() => !cancelled && launchError('Erreur lors du chargement des produits'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [params.toString()])

  const filtering = FILTERS.some(key => filters[key])

  const tbody = products.map(item => ({
    data: [
      item.name,
      item.provider,
      <Badge text={statusLabel(item.status)} color={statusColor(item.status)} />,
      formatParisDate(item.collecting_date),
      <span className='b_book_num'>{formatAmount(item.invested)} / {formatAmount(item.total)} {item.currency === 'USD' ? '$' : '€'}</span>,
      <span className='b_book_num'>{item.yield} %</span>,
      getTermSheetUrl(item)
        ? <a className='sp_link' href={getTermSheetUrl(item)} target='_blank' rel='noreferrer' onClick={(e) => e.stopPropagation()}>Voir</a>
        : <span className='sp_muted'>Non</span>,
    ],
    utils: { id: item._id, status: item.status }
  }))

  return (
    <>
      <div className='b_book_title_component'>
        <h1 className='b_book_page_title'>Produits</h1>
      </div>
      <p className='b_book_hint b_book_page_lede'>Tous les produits publiés. Cliquez sur un produit pour le clôturer ou ajouter son term sheet.</p>

      <div className='b_book_card'>
        <div className='b_book_toolbar'>
          <div className='b_book_toolbar_field b_book_toolbar_search'>
            <label htmlFor={`${id}-search`}>Nom</label>
            <input id={`${id}-search`} type='search' value={search} placeholder='Rechercher un produit' onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className='b_book_toolbar_field'>
            <label htmlFor={`${id}-status`}>Statut</label>
            <select id={`${id}-status`} value={filters.status} onChange={(e) => update({ status: e.target.value })}>
              <option value=''>Tous</option>
              {SP_STATUSES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className='b_book_toolbar_field'>
            <label htmlFor={`${id}-from`}>Collecte du</label>
            <input id={`${id}-from`} type='date' value={filters.from} max={filters.to || undefined} onChange={(e) => update({ from: e.target.value })} />
          </div>
          <div className='b_book_toolbar_field'>
            <label htmlFor={`${id}-to`}>au</label>
            <input id={`${id}-to`} type='date' value={filters.to} min={filters.from || undefined} onChange={(e) => update({ to: e.target.value })} />
          </div>
          <div className='b_book_toolbar_field'>
            <label htmlFor={`${id}-limit`}>Lignes</label>
            <select id={`${id}-limit`} value={limit} onChange={(e) => update({ limit: e.target.value })}>
              {LIMITS.map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          {filtering && (
            <button className='b_book_secondary_button sp_toolbar_reset' onClick={() => { setSearch(''); setParams({}, { replace: true }) }}>Réinitialiser</button>
          )}
        </div>

        {loading ? (
          <div className='pageLoaderBox'><PageLoader /></div>
        ) : tbody.length ? (
          <div className='table-container'>
            <Tableau
              thead={['Nom', 'Émetteur', 'Statut', 'Collecte', 'Investi / Total', 'Rendement', 'Term sheet']}
              tbody={tbody}
              hadLink
              linkPath={'/structured-products/product/'}
            />
            <BBookPager page={page} hasNext={products.length === limit} onChange={(value) => update({ page: value || '' })} />
          </div>
        ) : (
          <BBookEmpty
            title={filtering ? 'Aucun produit ne correspond à ces filtres' : 'Aucun produit publié'}
            hint={filtering ? 'Modifiez ou réinitialisez les filtres.' : "Un produit apparaît ici une fois son brouillon validé dans l'onglet Brouillons."}
          />
        )}
      </div>
    </>
  )
}

export default SpProducts
